// src/components/Layout.js
import React, { useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { BarChart3 } from "lucide-react"; 
import {
  LogOut,
  Home,
  PlusCircle,
  List,
  LayoutDashboard,
  Menu,
  ChevronLeft,
  BookOpen,
} from "lucide-react";
import "../styles/Layout.css";

export default function Layout({ children }) {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const toggleSidebar = () => {
    if (window.innerWidth < 768) {
      setMobileOpen(!mobileOpen);
    } else {
      setCollapsed(!collapsed);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 relative">
      {/* Sidebar */}
      <aside
        className={`
          fixed md:relative z-50 top-0 left-0 h-full
          ${collapsed ? "w-20" : "w-64"} 
          ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"} 
          bg-gradient-to-b from-indigo-600 to-purple-600 
          text-white flex flex-col p-4 shadow-lg 
          transition-all duration-300
        `}
      >
        <div className="flex items-center justify-between mb-6">
          {!collapsed && (
            <h2 className="text-2xl font-bold tracking-wide">
              InterviewTracker 🚀
            </h2>
          )}
          <button
            onClick={toggleSidebar}
            className="text-white hover:text-gray-200"
          >
            {collapsed ? <Menu size={20} /> : <ChevronLeft size={20} />}
          </button>
        </div>

        <nav className="flex-1 space-y-2">
          <NavItem icon={<Home size={20} />} text="Legacy Dashboard" to="/dashboard" collapsed={collapsed} />
          <NavItem icon={<LayoutDashboard size={20} />} text="Interview Board" to="/interview-board" collapsed={collapsed} />
          <NavItem icon={<PlusCircle size={20} />} text="Add Interview" to="/add" collapsed={collapsed} />
          <NavItem icon={<List size={20} />} text="View Interviews" to="/interviews" collapsed={collapsed} />
          <NavItem icon={<BookOpen size={20} />} text="Prep Materials" to="/prep" collapsed={collapsed} />
          <NavItem icon={<BarChart3 size={20} />} text="Analytics" to="/analytics" collapsed={collapsed} />
        </nav>

        <button
          onClick={handleLogout}
          className="mt-auto bg-white text-indigo-700 rounded-lg py-2 px-4 hover:bg-gray-100 flex items-center space-x-2 transition-colors"
        >
          <LogOut size={18} />
          {!collapsed && <span>Logout</span>}
        </button>
      </aside>

      {/* Overlay for Mobile */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 bg-black opacity-50 z-40 md:hidden"
        />
      )}

      {/* Main Content */}
      <main
        className={`flex-1 overflow-y-auto transition-all duration-300 ${
          collapsed ? "md:ml-20" : "md:ml-64"
        }`}
      >
        <header className="p-4 bg-white shadow-md flex justify-between items-center">
          <button
            onClick={toggleSidebar}
            className="md:hidden text-gray-800"
          >
            <Menu size={24} />
          </button>
          <h1 className="text-xl font-semibold text-gray-800">
            InterviewTracker
          </h1>
        </header>
        <div className="p-4 sm:p-6 md:p-8 max-w-6xl mx-auto w-full">
          {children}
        </div>
      </main>
    </div>
  );
}

function NavItem({ icon, text, to, collapsed }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center ${
          collapsed ? "justify-center" : "space-x-3"
        } text-lg p-2 rounded-lg transition-colors duration-200 ${
          isActive ? "bg-indigo-700" : "hover:bg-indigo-700"
        }`
      }
    >
      {icon}
      {!collapsed && <span>{text}</span>}
    </NavLink>
  );
}
